"use client";

import { memo, useMemo } from "react";
import {
  MoreVertical,
  FileText,
  Trash2,
  Pencil as Edit,
  Eye,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { type SavedReceipt } from "@/app/schema";
import dayjs from "dayjs";
import "dayjs/locale/he";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Link from "next/link";

dayjs.locale("he");
dayjs.extend(customParseFormat);

interface ReceiptCardProps {
  receipt: SavedReceipt;
  onDelete: (id: string) => void;
}

// Memoized ReceiptCard to prevent unnecessary re-renders when parent updates
// This follows the "rerender-memo" pattern
const ReceiptCardComponent = memo(function ReceiptCard({
  receipt,
  onDelete,
}: ReceiptCardProps) {
  const { invoiceData, createdAt, id } = receipt;

  // Memoize computed values to avoid recalculation on every render
  // This follows the "rerender-memo" pattern for expensive computations
  const receiptNumber = invoiceData.receiptNumber?.number || "-";
  const sellerName = invoiceData.seller?.name || "עוסק לא ידוע";
  const buyerName = invoiceData.buyer?.name || "לקוח לא ידוע";

  const totalAmount = useMemo(
    () =>
      invoiceData.items?.reduce(
        (sum: number, item) =>
          sum + (Number(item.amount) || 0) * (Number(item.netPrice) || 0),
        0,
      ) || 0,
    [invoiceData.items],
  );

  const date = useMemo(() => {
    const dateValue = invoiceData.dateOfIssue || createdAt;
    if (!dateValue) return "-";

    // Try parsing as Israeli format (DD/MM/YYYY) first
    let parsed = dayjs(dateValue, "DD/MM/YYYY", true);
    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }

    // Try ISO format (createdAt is stored as ISO)
    parsed = dayjs(dateValue);
    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }

    return "-";
  }, [invoiceData.dateOfIssue, createdAt]);

  return (
    <div className="group relative flex flex-col gap-3 rounded-none border border-neutral-300 bg-white p-3 transition-all duration-200 ease-out hover:border-neutral-900 sm:p-4">
      {/* Header with receipt number and menu */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-none border border-neutral-200 bg-neutral-100 sm:h-10 sm:w-10">
            <FileText
              className="h-4 w-4 text-neutral-500 sm:h-5 sm:w-5"
              strokeWidth={1.5}
            />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="truncate text-sm font-semibold text-neutral-900 sm:text-base"
              dir="rtl"
            >
              קבלה {receiptNumber}
            </h3>
            <p className="text-xs text-neutral-500">{date}</p>
          </div>
        </div>

        {/* Menu button */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0 rounded-none border border-transparent opacity-0 transition-opacity group-hover:opacity-100 hover:border-neutral-200"
              style={{ contain: "layout" }}
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="w-40 rounded-none border-neutral-300 p-1"
          >
            <div className="flex flex-col gap-1">
              <Link href={`/receipt/${id}`} prefetch={true}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2"
                >
                  <Eye className="h-4 w-4" />
                  צפייה
                </Button>
              </Link>
              <Link href={`/create-receipt?edit=${id}`} prefetch={true}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2"
                >
                  <Edit className="h-4 w-4" />
                  עריכה
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(id)}
                className="w-full justify-start gap-2 text-red-600 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
                מחיקה
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Receipt details */}
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-500">מקבל:</span>
          <span className="font-medium text-neutral-700">{sellerName}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-neutral-500">מחייב:</span>
          <span className="font-medium text-neutral-700">{buyerName}</span>
        </div>
      </div>

      {/* Amount */}
      <div className="mt-auto flex items-center justify-between border-t border-neutral-100 pt-3">
        <span className="text-sm text-neutral-500">סכום:</span>
        <span className="text-lg font-bold text-neutral-900">
          ₪{totalAmount.toLocaleString("he-IL")}
        </span>
      </div>
    </div>
  );
});

// Export both named versions for backward compatibility
export { ReceiptCardComponent as ReceiptCard };
export default ReceiptCardComponent;
